import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";

export interface ProductUnit {
  id: number;
  sigla?: string;
  descricao?: string;
  isPrincipal: boolean;
  isPadrao: boolean;
  vendaFracionada: boolean;
  isAtivo: boolean;
  fatorConversao?: number;
  volume?: number;
  peso?: number;
  unidadeVolume?: string;
  unidadePeso?: string;
}

export interface ProductUnitFormData {
  sigla?: string;
  descricao?: string;
  isPrincipal?: boolean;
  isPadrao?: boolean;
  vendaFracionada?: boolean;
  isAtivo?: boolean;
  fatorConversao?: number;
  volume?: number;
  peso?: number;
  unidadeVolume?: string;
  unidadePeso?: string;
}

export function useProductUnits() {
  const queryClient = useQueryClient();

  const { data: units = [], isLoading } = useQuery({
    queryKey: ["product-units"],
    queryFn: async () => {
      const { data } = await apiClient.get<ProductUnit[]>("/api/unidades-produto");
      return data;
    },
  });

  const createUnit = useMutation({
    mutationFn: async (formData: ProductUnitFormData) => {
      const { data } = await apiClient.post<ProductUnit>("/api/unidades-produto", formData);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product-units"] });
      toast.success("Unidade criada com sucesso!");
    },
    onError: (error: Error) => {
      toast.error("Erro ao criar unidade: " + error.message);
    },
  });

  const updateUnit = useMutation({
    mutationFn: async ({ id, ...formData }: ProductUnitFormData & { id: number }) => {
      const { data } = await apiClient.put<ProductUnit>(`/api/unidades-produto/${id}`, formData);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product-units"] });
      toast.success("Unidade atualizada com sucesso!");
    },
    onError: (error: Error) => {
      toast.error("Erro ao atualizar unidade: " + error.message);
    },
  });

  const deleteUnit = useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/api/unidades-produto/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product-units"] });
      toast.success("Unidade inativada com sucesso!");
    },
    onError: (error: Error) => {
      toast.error("Erro ao inativar unidade: " + error.message);
    },
  });

  return {
    units,
    isLoading,
    createUnit,
    updateUnit,
    deleteUnit,
  };
}
